import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getPmIntegrationStatus, importPmProject, listPmProjects } from "../api";
import type { CaseStatus, PmProjectItem } from "../types";

type Filter = "ALL" | "UNIMPORTED" | "IMPORTED" | "CHANGED";

const statusLabels: Record<CaseStatus, string> = {
  DRAFT: "草稿",
  IN_REVIEW: "待审核",
  PUBLISHED: "已发布",
  ARCHIVED: "已归档",
};

export function PmProjectImport() {
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [source, setSource] = useState("https://pm.vhuiwu.cn");
  const [items, setItems] = useState<PmProjectItem[]>([]);
  const [keyword, setKeyword] = useState("");
  const [filter, setFilter] = useState<Filter>("ALL");
  const [loading, setLoading] = useState(false);
  const [workingId, setWorkingId] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  async function load(search = keyword) {
    setLoading(true); setError("");
    try {
      const result = await listPmProjects(search);
      setItems(result.items); setSource(result.source);
    } catch (reason) {
      setError(`读取 PM 项目失败：${(reason as Error).message}`);
    } finally { setLoading(false); }
  }

  useEffect(() => {
    getPmIntegrationStatus().then((result) => {
      setConfigured(result.configured); setSource(result.source);
      if (result.configured) void load("");
    }).catch((reason) => { setConfigured(false); setError((reason as Error).message); });
  }, []);

  const visibleItems = useMemo(() => items.filter((item) => {
    if (filter === "UNIMPORTED") return !item.imported;
    if (filter === "IMPORTED") return item.imported;
    if (filter === "CHANGED") return item.hasSourceChanges;
    return true;
  }), [filter, items]);

  const counts = useMemo(() => ({
    all: items.length,
    unimported: items.filter((item) => !item.imported).length,
    imported: items.filter((item) => item.imported).length,
    changed: items.filter((item) => item.hasSourceChanges).length,
  }), [items]);

  async function sync(item: PmProjectItem) {
    setWorkingId(item.sourceRecordId); setNotice(""); setError("");
    try {
      const result = await importPmProject(item.sourceRecordId);
      setNotice(result.created
        ? `${item.title} 已生成案例草稿，并关联 ${result.syncedAssets} 个私有素材记录。`
        : `${item.title} 的 PM 来源快照已刷新，案例编辑内容没有被覆盖。`);
      await load(keyword);
    } catch (reason) {
      setError(`同步失败：${(reason as Error).message}`);
    } finally { setWorkingId(""); }
  }

  return <section>
    <div className="admin-heading pm-heading">
      <div><span className="eyebrow">PM SOURCE</span><h1>PM 项目接入</h1><p>从项目管理系统生成线下活动案例草稿；同步素材保持私有，发布仍需人工审核。</p></div>
      <a className="secondary-button" href={`${source.replace(/\/$/, "")}/cases`} target="_blank" rel="noreferrer">打开 PM 案例库</a>
    </div>

    <div className="integration-rule">
      <strong>数据边界</strong>
      <span>PM 管理项目过程和原始素材；案例库管理编辑、授权审核与公开发布。刷新 PM 来源不会覆盖已编辑的案例内容。</span>
    </div>

    {configured === false && <div className="integration-warning">
      <strong>PM 服务端连接尚未配置</strong>
      <p>需要在案例库 API 的私有环境变量中配置 PM 专用账号。账号和密码不会发送到浏览器，也不会写入代码仓库。</p>
    </div>}
    {notice && <p className="notice">{notice}</p>}
    {error && <p className="form-error integration-error">{error}</p>}

    <div className="pm-summary" aria-label="PM 项目同步概览">
      <button className={filter === "ALL" ? "active" : ""} onClick={() => setFilter("ALL")}><strong>{counts.all}</strong><span>全部项目</span></button>
      <button className={filter === "UNIMPORTED" ? "active" : ""} onClick={() => setFilter("UNIMPORTED")}><strong>{counts.unimported}</strong><span>待生成草稿</span></button>
      <button className={filter === "IMPORTED" ? "active" : ""} onClick={() => setFilter("IMPORTED")}><strong>{counts.imported}</strong><span>已关联案例</span></button>
      <button className={filter === "CHANGED" ? "active" : ""} onClick={() => setFilter("CHANGED")}><strong>{counts.changed}</strong><span>PM 来源有更新</span></button>
    </div>

    <div className="toolbar pm-toolbar">
      <input placeholder="搜索项目、客户、项目编号或地点" value={keyword} onChange={(event) => setKeyword(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void load(); }} />
      <button disabled={!configured || loading} onClick={() => void load()}>{loading ? "正在读取…" : "刷新 PM 项目"}</button>
    </div>

    <div className="admin-table-wrap pm-table-wrap"><table className="admin-table pm-table"><thead><tr>
      <th>PM 项目</th><th>活动信息</th><th>素材</th><th>案例状态</th><th>操作</th>
    </tr></thead><tbody>
      {visibleItems.map((item) => <tr key={item.sourceRecordId}>
        <td><strong>{item.title}</strong><small>{item.clientName || "客户名称待补充"} · {item.projectCode}</small></td>
        <td><span>{item.activityTime || "时间待补充"}</span><small>{item.activityLocation || "地点待补充"}</small></td>
        <td><span className="asset-count">{item.assetCount} 个</span><small>同步后默认私有</small></td>
        <td>{item.imported
          ? <><span className={`status ${(item.caseStatus || "DRAFT").toLowerCase()}`}>{statusLabels[item.caseStatus || "DRAFT"]}</span>{item.hasSourceChanges && <small className="source-changed">PM 来源有更新</small>}</>
          : <span className="status">未生成</span>}</td>
        <td className="actions pm-actions">
          {item.caseId && <Link to={`/admin/cases/${item.caseId}`}>编辑案例</Link>}
          <button disabled={workingId === item.sourceRecordId} onClick={() => void sync(item)}>{workingId === item.sourceRecordId ? "同步中…" : item.imported ? "刷新来源" : "生成草稿"}</button>
          <a href={item.sourceUrl} target="_blank" rel="noreferrer">查看 PM</a>
        </td>
      </tr>)}
      {!visibleItems.length && <tr><td colSpan={5}><div className="empty-panel">{loading ? "正在读取 PM 项目" : configured ? "当前筛选条件下没有项目" : "配置服务端连接后即可读取 PM 项目"}</div></td></tr>}
    </tbody></table></div>
  </section>;
}
